<template>
  <div class="creative-test-overview w-full flex flex-col gap-1 bg-neutral-25">
    <!-- Graph Area -->
    <div class="graph-area">
      <div v-if="fetchingData || !chartDataLoaded" class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
      style="z-index: 1000;">
        <BaseLoadingSpinnerCircle class="text-icon-normal" :duration="1" :width="36" :height="28" />
      </div>
      <CreativeTestScatterChart 
        v-if="chartDataLoaded && chartData.length"
        class="fade-in"
        :lens="lens"
        :data="chartData"
        :thresholds="chartThresholds"
        :max-values="maxChartValues"
        :hovered-category="hoveredCategory"
        :is-active-tab="isActiveTab"
        @show-insights-drawer="$emit('show-insights-drawer', $event)"
      />
      <!-- No Data -->
      <div v-if="!fetchingData && !chartData.length" class="absolute w-full h-full flex flex-col items-center justify-center fade-in">
        <div class="flex flex-col bg-neutral-malpha-50 py-4 px-4 rounded-lg text-center"
        style="max-width: 100%">
          <BaseText type="label" size="sm" class="text-white">
            No Launched Creatives Found
          </BaseText>
          <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-2">
            You haven't launched any creatives within the {{ dateFilterLabel.toLowerCase() }}.
          </BaseText>
          <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-0.5">
            Try changing your date range to view more data.
          </BaseText>
        </div>
      </div>
    </div>
    <!-- Summary Area -->
    <div class="w-full grid grid-cols-4 gap-x-1">
      <!-- Insight Category Atoms -->
      <div v-for="(category, index) in insightCategories" :key="`category-${category.key}`" class="w-full flex items-center">
        <!-- divider -->
        <div v-if="index > 0" class="mr-1 border-l border-border-normal" style="height: 100px" />
        <!-- atom -->
        <BaseTooltipV2 
          class="w-full"
          :tooltip-enabled="selectedCategoryGroup !== category.key"
          :text="category.tooltip"
          position="cursor"
          offset="12"
          :delay="1000"
        >
          <button class="category-atom" 
          :class="{
            'active': selectedCategoryGroup === category.key,
            'first': index === 0,
            'last': index === insightCategories.length - 1
          }"
          @mouseenter="hoveredCategory = category.key" @mouseleave="hoveredCategory = null" 
          @click="handleCategorySelect(category.key)">
            <!-- Active Indicator -->
            <div class="active-indicator"><div class="inner"/></div>
            <!-- Label -->
            <div class="flex items-center gap-1.5 z-10">
              <component :is="category.icon" class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-normal">
                {{ category.label }}
              </BaseText>
            </div>
            <!-- Count -->
            <div v-if="fetchingData" class="w-14 h-8 rounded-md skeleton opacity-70 z-10" />
            <BaseText v-else-if="category.showValue" type="display" size="2xs" class="text-text-muted z-10 fade-in">
              {{ category.count }}
            </BaseText>
            <BaseText v-else type="display" size="2xs" class="text-text-subdued">
              —
            </BaseText>
            <!-- Previous Period Change -->
            <IntegerChangeIndicator 
              class="z-10"
              :change-value="category.periodDiff || 0" 
              :direction="category.changeDirection" 
              :loading="fetchingData" 
              :hide-value="selectedCategoryGroup !== 'all'"
            />
          </button>
        </BaseTooltipV2>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import CreativeTestScatterChart from './scatter/CreativeTestScatterChart.vue'
import PercentageChangeIndicator from '../PercentageChangeIndicator.vue'
import IntegerChangeIndicator from '../IntegerChangeIndicator.vue'

// Icons
import NewLaunchedIcon from '../../globals/Icons/NewLaunchedIcon.vue'
import ScaleIcon from '../../globals/Icons/ScaleIcon.vue'
import WinnerIcon from '../../globals/Icons/WinnerIcon.vue'
import LoserIcon from '../../globals/Icons/SignDirectionIcon.vue'

export default {
  name: 'LensCreativeTestOverview',
  components: {
    CreativeTestScatterChart,
    PercentageChangeIndicator,
    IntegerChangeIndicator
  },
  props: {
    lens: {
      type: Object,
    },
    selectedCategoryGroup: {
      type: String,
      required: true
    },
    chartData: {
      type: Array
    },
    maxChartValues: {
      type: Object    
    },
    categoryInsightCounts: {
      type: Object
    },
    chartThresholds: {
      type: Object
    },
    fetchingData: {
      type: Boolean,
      default: false
    },
    dateFilterLabel: {
      type: String,
      default: ''
    },
    isActiveTab: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      hoveredCategory: null
    }
  },
  computed: {
    insightCategories () {
      return [
        {
          key: 'all',
          label: 'Launched (all)',
          icon: NewLaunchedIcon,
          count: this.categoryInsightCounts?.launched?.current,
          periodDiff: this.insightPeriodDiffs?.launched,
          changeDirection: 'positive',
          tooltip: 'View all launched creatives',
          showValue: this.selectedCategoryGroup === 'all'
        },
        {
          key: 'scaling',
          label: 'Scaling',
          icon: ScaleIcon,
          count: this.categoryInsightCounts?.scaling?.current,
          periodDiff: this.insightPeriodDiffs?.scaling,
          changeDirection: 'positive',
          tooltip: 'Filter for scaling creatives',
          showValue: ['all', 'scaling'].includes(this.selectedCategoryGroup)
        },
        {
          key: 'winners',
          label: 'Winners',
          icon: WinnerIcon,
          count: this.categoryInsightCounts?.winners?.current,
          periodDiff: this.insightPeriodDiffs?.winners,
          changeDirection: 'positive',
          tooltip: 'Filter for winning creatives',
          showValue: ['all', 'winners'].includes(this.selectedCategoryGroup)
        },
        {
          key: 'losers',
          label: 'Losers',
          icon: LoserIcon,
          count: this.categoryInsightCounts?.losers?.current,
          periodDiff: this.insightPeriodDiffs?.losers,
          changeDirection: 'negative',
          tooltip: 'Filter for losing creatives',
          showValue: ['all', 'losers'].includes(this.selectedCategoryGroup)
        }
      ]
    },
    insightPeriodDiffs () {
      if (!this.categoryInsightCounts) return null
      const { launched, scaling, winners, losers } = this.categoryInsightCounts
      
      return {
        launched: launched?.current - launched?.previous,
        scaling: scaling?.current - scaling?.previous,
        winners: winners?.current - winners?.previous,
        losers: losers?.current - losers?.previous
      }
    },
    chartDataLoaded () {
      return this.maxChartValues && this.chartThresholds
    }
  },
  methods: {
    handleCategorySelect (category) {
      this.hoveredCategory = null
      this.$emit('filter-by-category', category)
    }
  }
}
</script>

<style scoped>
.creative-test-overview {
  border-radius: 18px;
  padding: 4px;
}
.graph-area {
  position: relative;
  width: 100%;
  height: 336px;
  background-color: white;
  border-radius: 14px;
  box-shadow: 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.04);
}

.category-atom {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 14px;
  transition: background-color 200ms ease-in-out, box-shadow 200ms ease-in-out;
  box-shadow: none;
  background-color: transparent;
}
.category-atom:hover:not(.active) {
  background-color: #ECEFF3;
}
.category-atom.active {
  background-color: transparent;
  pointer-events: none;
}
.category-atom .active-indicator {
  position: absolute;
  top: -4px;
  bottom: -4px;
  left: -32px;
  right: -32px;
  overflow-y: hidden;
  border-radius: 14px;
  pointer-events: none;
}
.category-atom.first .active-indicator { left: -4px; }
.category-atom.last .active-indicator { right: -4px; }
.category-atom .active-indicator .inner {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 28px;
  right: 28px;
  border-radius: 14px;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.category-atom.first .active-indicator .inner { left: 4px; }
.category-atom.last .active-indicator .inner { right: 4px; }
.category-atom.active .active-indicator .inner {
  background-color: white;
  box-shadow: 0 160px 64px 0 rgba(46, 53, 73, 0.02), 0 90px 54px -8px rgba(46, 53, 73, 0.04), 0 40px 40px -12px rgba(46, 53, 73, 0.07), 0 10px 22px -8px rgba(46, 53, 73, 0.16);
}
.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
</style>